"use client";

import { ArrowRightIcon } from "@phosphor-icons/react";
import Image from "next/image";
import Link from "next/link";

import Paragraph from "~/componentsDS/Typography/Paragraph";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import type { Journal } from "~/lib/medicalJournals/getMedicalJournals";

type MedicalJournalCardProps = {
    journal: Journal;
};

export default function MedicalJournalCard({
    journal,
}: MedicalJournalCardProps) {
    const sendEvent = useTrackEvent();

    const handleClick = () => {
        sendEvent({
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.HP_INTERACTION,
                widget: "medical_journals",
                meta_data: {
                    click_text: journal.title,
                    product_name: "general",
                },
            },
        }, journal.link);
    };

    return (
        <Link
            href={journal.link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            className="group flex h-[320px] flex-col overflow-hidden rounded-xl bg-gray100"
        >
            <div className="relative h-[160px] shrink-0 overflow-hidden">
                <Image
                    src={journal.imageUrl}
                    alt=""
                    fill
                    sizes="300px"
                    loading="lazy"
                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                />
            </div>

            <div className="flex min-h-0 flex-1 flex-col p-4">
                <Paragraph
                    as="h3"
                    weight="500"
                    className="line-clamp-2"
                    title={journal.title}
                >
                    {journal.title}
                </Paragraph>
                <Paragraph
                    className="line-clamp-2 mt-1"
                >
                    {journal.description}
                </Paragraph>

                <div className="mt-auto flex items-center justify-between">
                    {journal.readTime && (
                        <Paragraph size="sm">{journal.readTime} min read</Paragraph>
                    )}
                    <span className="ml-auto flex size-8 items-center justify-center rounded-full transition-colors group-hover:bg-primary100">
                        <ArrowRightIcon aria-hidden="true" size={20} />
                    </span>
                </div>
            </div>
        </Link>
    );
}
